import { Link } from "react-router-dom";
import {
  Smartphone, Wifi, Clock, Monitor, Camera, Printer, KeyRound,
  Phone, CheckCircle2, ShieldCheck, ClipboardCheck, PhoneCall, Home as HomeIcon,
} from "lucide-react";
import { CONTACT } from "../data";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

const PRICED = [
  {
    icon: Smartphone,
    title: "Phone Setup",
    price: "$79",
    unit: "flat",
    blurb: "New phone setup & data transfer from your old device.",
    includes: ["Move photos, contacts & apps", "Set up email & favorite apps", "A quick, friendly walkthrough"],
  },
  {
    icon: Wifi,
    title: "Wi‑Fi Setup",
    price: "$99",
    unit: "flat",
    blurb: "Reliable internet in every room of your home.",
    includes: ["Router & modem setup", "Fix weak signal & dead zones", "Secure your network"],
    featured: true,
  },
  {
    icon: Clock,
    title: "Hourly Support",
    price: "$60",
    unit: "per hour",
    blurb: "For lessons, coaching, or anything else you need help with.",
    includes: ["1‑on‑1 tech lessons", "Troubleshooting & fixes", "Go at your own pace"],
  },
];

const QUOTE_SERVICES = [
  { icon: Monitor, title: "Computer & Laptop Setup" },
  { icon: Camera, title: "Ring Camera & Smart Home Install" },
  { icon: Printer, title: "Printer Setup" },
  { icon: KeyRound, title: "Email & Password Help" },
];

const PROMISES = [
  { icon: ShieldCheck, title: "No hidden fees", text: "The price we quote is the price you pay." },
  { icon: ClipboardCheck, title: "You approve first", text: "We always agree on the cost before any work begins." },
  { icon: PhoneCall, title: "Free friendly quote", text: "Call us for a no‑pressure price over the phone." },
  { icon: HomeIcon, title: "In‑home service", text: "We come to you across the Columbus area." },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <PageHero
        eyebrow="Simple, Honest Pricing"
        title="Clear prices, no surprises"
        subtitle="You'll always know the cost before we start. Here are our most popular services — for anything else, just call and we'll give you a friendly quote."
      >
        <a
          href={CONTACT.phoneHref}
          data-testid="pricing-call"
          className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
        >
          <Phone className="h-5 w-5" /> Call for a quote — {CONTACT.phone}
        </a>
      </PageHero>

      {/* Priced services */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {PRICED.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div
                data-testid={`price-card-${i}`}
                className={`relative h-full rounded-3xl p-8 lg:p-10 flex flex-col ${
                  p.featured
                    ? "bg-primary text-white shadow-2xl lg:-translate-y-4"
                    : "bg-card border border-border"
                }`}
              >
                {p.featured && (
                  <span className="absolute top-6 right-6 rounded-full bg-accent text-white text-xs font-bold px-3 py-1">
                    Most popular
                  </span>
                )}
                <span
                  className={`grid place-items-center h-14 w-14 rounded-2xl mb-6 ${
                    p.featured ? "bg-accent text-white" : "bg-accent/10 text-accent"
                  }`}
                >
                  <p.icon className="h-7 w-7" />
                </span>
                <h3 className={`font-heading font-bold text-2xl ${p.featured ? "text-white" : "text-foreground"}`}>
                  {p.title}
                </h3>
                <p className={`mt-2 leading-relaxed ${p.featured ? "text-slate-300" : "text-muted-foreground"}`}>
                  {p.blurb}
                </p>
                <div className="mt-6 flex items-end gap-2">
                  <span className={`font-heading font-extrabold text-5xl ${p.featured ? "text-white" : "text-foreground"}`}>
                    {p.price}
                  </span>
                  <span className={`mb-2 font-semibold ${p.featured ? "text-slate-300" : "text-muted-foreground"}`}>
                    {p.unit}
                  </span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.includes.map((inc) => (
                    <li key={inc} className={`flex items-start gap-3 ${p.featured ? "text-slate-200" : "text-slate-300"}`}>
                      <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" /> {inc}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`price-book-${i}`}
                  className={`mt-8 inline-flex items-center justify-center h-14 rounded-full font-bold text-lg transition-colors ${
                    p.featured
                      ? "bg-accent text-white hover:bg-[hsl(196,82%,27%)]"
                      : "bg-primary text-white hover:bg-slate-800"
                  }`}
                >
                  Book a Free Visit
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-8 text-center text-muted-foreground text-lg">
          Prices are for standard in‑home visits in the Columbus area. Parts or equipment (if needed) are extra and always discussed first.
        </p>
      </section>

      {/* Custom quote services */}
      <section className="bg-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <div className="max-w-2xl">
              <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl tracking-tight leading-snug">
                Need something else?
              </h2>
              <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
                Every home is a little different, so these services are priced with a
                quick, friendly quote. Give us a call and we'll give you a clear price up front.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {QUOTE_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.08}>
                <div className="rounded-2xl border border-border bg-card p-7 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-accent/10 text-accent shrink-0">
                      <s.icon className="h-6 w-6" />
                    </span>
                    <h3 className="font-heading font-bold text-lg text-foreground">{s.title}</h3>
                  </div>
                  <span className="text-accent font-bold whitespace-nowrap">Custom quote</span>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10">
            <a
              href={CONTACT.phoneHref}
              data-testid="pricing-quote-call"
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
            >
              <Phone className="h-5 w-5" /> Get a free quote
            </a>
          </div>
        </div>
      </section>

      {/* Promises */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROMISES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-border bg-card p-8">
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent/10 text-accent mb-5">
                  <p.icon className="h-7 w-7" />
                </span>
                <h3 className="font-heading font-bold text-xl text-foreground mb-2">{p.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
